<template>
  <div class="checkout mb-4">
    <div class="top">
      <h1 class="title m-0 mt-2">{{ title }}</h1>
      <h2 class="subtitle m-0">{{ subtitle }}</h2>
    </div>
    <div class="content mt-3">
      <BillingContentColumn class="billing-data" :title="__('Dados de faturamento')">
        <template v-slot:separator-icon>
          <i style="margin-top: 1px;" class="fa fa-chevron-right"></i>
        </template>
        <template v-slot:content>
          <div class="billing-data-inner mt-2" :class="{ opaque: isStageUnreached('billing-data') }">
            <BillingDataForm
              :show-button="stage === 'billing-data'"
              :disabled="isStageUnreached('billing-data')"
              :submitting="submitting"
              :errors="billingData.errors"
              @updated="(data) => billingData.data = data"
              @advance="handleBillingDataSubmit"
            />
          </div>
        </template>
      </BillingContentColumn>
      <BillingContentColumn class="payment-method" :title="__('Método de pagamento')">
        <template v-slot:separator-icon>
          <i style="margin-top: 1px;" class="fa fa-chevron-right"></i>
        </template>
        <template v-slot:content>
          <div class="payment-method-inner mt-2rem" :class="{ opaque: isStageUnreached('payment-method') }">
            <BillingPaymentMethod
              :show-button="stage === 'payment-method'"
              :disabled="isStageUnreached('payment-method')"
              :submitting="submitting"
              :initial-payment-method="subscribed ? null : 'credit-card'"
              @updated="(data) => paymentMethodData = data"
              @advance="handlePaymentMethodSubmit"
            />
          </div>
        </template>
      </BillingContentColumn>
      <BillingContentColumn class="finish-checkout" :title="__('Resumo da compra')">
        <template v-slot:separator-icon>
          <i class="fa fa-check"></i>
        </template>
        <template v-slot:content>
          <div class="finish-checkout-inner mt-2rem" :class="{ opaque: isStageUnreached('finish-checkout') }">
            <BillingCouponForm
              :disabled="isStageUnreached('finish-checkout')"
              @coupon:changed="(data) => finishCheckoutData.coupon = data"
            />
            <BillingPurchaseSummary
              :plan-name="plan.name"
              :value="plan.price"
              :discount="discount"
            />
            <div class="terms-container">
              <InputCheckboxSingle
                id="terms"
                :checked="terms"
                :disabled="isStageUnreached('finish-checkout')"
                @checked="(state) => terms = state"
              />
              <label
                class="mb-0"
              >{{ __('Eu li e aceito os') }}
                <a 
                  href="https://salesflowcrm.movidesk.com/kb/article/405957/termos-de-uso"
                  target="_blank"  
                >
                  {{ __('termos de uso') }}
                </a>
              </label>
            </div>
            <BillingConfirmationPlanUpgradeModalTrigger
              :disabled="isStageUnreached('finish-checkout')"
              :submitting="submitting"
              :success="finishCheckoutData.success"
              @advance="handleFinishCheckoutSubmit"
            />
          </div>
        </template>
      </BillingContentColumn>
    </div>
  </div>
</template>

<script>
import payment from '../../mixins/payment';
import showUpgradePlanAlert from '../../mixins/showUpgradePlanAlert';

export default {
  mixins: [payment, showUpgradePlanAlert],

  props: {
    plan: {
      type: Object,
      required: true,
    },
    subscribed: {
      type: Boolean,
      required: true,
    },
  },

  data () {
    return {
      currentStageIndex: 0,
      stages: [
        { id: 'billing-data' },
        { id: 'payment-method' },
        { id: 'finish-checkout' },
      ],
      billingData: {
        data: null,
        errors: {},
      },
      paymentMethodData: {
        method: null,
        token: null,
        data: null,
      },
      finishCheckoutData: {
        coupon: null,
        success: false,
      },
      terms: false,
      submitting: false,
    };
  },

  computed: {
    stage () {
      return this.stages[this.currentStageIndex].id;
    },

    discount () {
      if (!this.finishCheckoutData.coupon) {
        return null;
      }

      return {
        type: this.finishCheckoutData.coupon.percent_off ? 'percent' : 'amount',
        value: this.finishCheckoutData.coupon.percent_off ? this.finishCheckoutData.coupon.percent_off : this.finishCheckoutData.coupon.amount_off,
      };
    },

    title () {
      if (this.subscribed) {
        return this.__('Minha assinatura');
      } else {
        return this.__('Pagamento');
      }
    },

    subtitle () {
      if (this.subscribed) {
        return this.__('Confirme seus dados e finalize seu upgrade.');
      } else {
        return this.__('Atualize seus dados');
      }
    },
  },

  methods: {
    advance () {
      this.currentStageIndex = Math.min(this.currentStageIndex + 1, this.stages.length - 1);
    },

    isStageUnreached (id) {
      const stageIndex = this.stages.findIndex((stage) => stage.id === id);

      return stageIndex > this.currentStageIndex;
    },

    handleBillingDataSubmit (data) {
      this.billingData.data = data;

      this.advance();
    },

    async handlePaymentMethodSubmit (data) {
      this.paymentMethodData = data;

      this.advance();
    },

    async handleFinishCheckoutSubmit () {
      if (!this.terms) {
        return Alert.error(this.__('Você deve aceitar os termos de uso para continuar'));
      }

      this.submitting = true;

      try {
        const billingData = this.getBillingData();
        const paymentMethodData = await this.getPaymentMethodData();
        const finishCheckoutData = this.getFinishCheckouData();

        const payload = {
          billing_data: billingData,
          payment_method_data: paymentMethodData,
          subscription_data: finishCheckoutData,
        };

        if (this.subscribed) {
          this.upgradePlan(payload).then(({ body }) => {
            this.showConfirmationAlert(body, '/billing');
          }).catch((response) => {
            console.error(response);

            if (response.status === 422) {
              this.billingData.errors = this.extractBillingDataErrors(response.data);
            }

            if (
              (response.status === 422
              || response.status === 400)
              && response.data.message
            ) {
              return Alert.error(this.__(response.data.message));
            }

            return Alert.error(this.__('Erro ao fazer upgrade de plano'));
          }).finally(() => {
            this.submitting = false;
          });
        } else {
          this.subscribe(payload).then((response) => {
            setTimeout(() => {
              window.location = '/billing';
            }, 1500)
          }).catch((response) => {
            console.error(response);

            if (response.status === 422) {
              this.billingData.errors = this.extractBillingDataErrors(response.data);
            }

            if (
              (response.status === 422
              || response.status === 400)
              && response.data.message
            ) {
              return Alert.error(this.__(response.data.message));
            }

            return Alert.error(this.__('Ocorreu um erro ao criar a sua assinatura'));
          }).finally(() => {
            this.submitting = false;
          });
        }
      } catch (error) {
        console.error(error);

        this.submitting = false;

        Alert.error(this.__('Ocorreu um erro inesperado, verifique os dados sendo enviados'));
      }
    },

    getBillingData() {
      return this.billingData.data;
    },

    async getPaymentMethodData() {
      if (this.paymentMethodData.method === 'bankslip') {
        return {
          method: this.paymentMethodData.method,
          token: null,
        };
      }

      if (!this.paymentMethodData.anyFieldsFilled && this.paymentMethodData.alreadyExists) {
        return {
          method: null,
          token: null,
        };
      }

      const payload = {
        number: this.paymentMethodData.data.number,
        month: this.paymentMethodData.data.validity?.get('month') + 1,
        year: this.paymentMethodData.data.validity?.get('year'),
        firstName: this.paymentMethodData.data.firstName,
        lastName: this.paymentMethodData.data.lastName,
        securityCode: this.paymentMethodData.data.securityCode,
      };

      const token = await this.generateToken(payload);

      return {
        method: this.paymentMethodData.method,
        token: token,
      };
    },

    getFinishCheckouData() {
      return {
        plan: this.plan.provider_id,
        coupon: this.finishCheckoutData.coupon?.code,
        terms: this.terms,
      };
    },

    subscribe (payload) {
      return this.$http.post('/billing/subscription/create', payload);
    },

    upgradePlan (payload) {
      return this.$http.post('/billing/subscription/upgrade', payload);
    },

    extractBillingDataErrors (errors) {
      let obj = {};
      
      for (const [key, value] of Object.entries(errors)) {
        if (!key.startsWith('billing_data.')) {
          continue;
        }

        obj[key.replace('billing_data.', '')] = value;
      }

      return obj;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../sass/variables';
@import '../../../sass/mixins';

.checkout {
  .top {
    margin-left: 1rem;

    .title {
      @include salesflow-text($color: $salesflow-font-gray, $font-weight: 700);
      text-transform: uppercase;
    }

    .subtitle {
      @include salesflow-title($font-weight: 500);
      font-size: 1.75rem;
    }
  }

  .content {
    display: flex;
    gap: 2.5rem;
    padding: 1.5rem;
    background: $salesflow-background-content;
    border: 1px solid $salesflow-outline-gray-medium;
    border-radius: 24px;

    .finish-checkout {
      .finish-checkout-inner {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }

      .terms-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        label {
          @include salesflow-text;

          a {
            font-weight: 600;
            text-decoration: underline;
          }
        }
      }
    }
  }

  .mt-2rem {
    margin-top: 2rem;
  }

  .opaque {
    opacity: 0.5;
  }
}

</style>
